import { useState } from "react";
import { Check, ChevronDown, ChevronRight, Circle } from "lucide-react";
import { formatElapsed } from "./ProgressSteps";

const THINKING_LABEL = "Analizando lo que encontré"; // aviso de «pensando» de respuestas viejas: no es un paso del trabajo

// Pasos seguidos con el mismo título (p. ej. 12 lecturas del servidor) se muestran como una sola línea con «× 12» y se despliegan al pulsarla.
function groupSteps(steps) {
  const groups = [];
  steps.forEach((step) => {
    const last = groups[groups.length - 1];
    if (last && last.label === step.label) last.items.push(step);
    else groups.push({ label: step.label, items: [step] });
  });
  return groups;
}

// «▸ Ver lo que hizo»: los pasos y el plan que siguió el Orquestador, guardados con su respuesta y plegados por defecto.
export function ActivityLog({ activity }) {
  const [open, setOpen] = useState(false);
  const [wide, setWide] = useState({});
  const [groupOpen, setGroupOpen] = useState({});
  const steps = (Array.isArray(activity?.steps) ? activity.steps : []).filter((s) => s.label !== THINKING_LABEL);
  const groups = groupSteps(steps);
  const plan = Array.isArray(activity?.plan) ? activity.plan : [];
  if (steps.length === 0 && plan.length === 0) return null;
  const seconds = Number.isFinite(activity.seconds) ? activity.seconds : 0;
  const Icon = open ? ChevronDown : ChevronRight;

  return (
    <div data-testid="activity-log" className="mb-2 text-sm text-muted-foreground">
      <button
        type="button"
        data-testid="activity-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs hover:bg-muted"
      >
        <Icon className="h-3.5 w-3.5" />
        <span>{open ? "Ocultar lo que hizo" : "Ver lo que hizo"}</span>
        <span className="tabular-nums opacity-70">
          · {steps.length} paso{steps.length === 1 ? "" : "s"}
          {seconds > 0 ? ` · ${formatElapsed(seconds)}` : ""}
        </span>
      </button>
      {open && (
        <div data-testid="activity-body" className="mt-1.5 space-y-1.5 rounded-lg border border-border px-3 py-2">
          {plan.length > 0 && (
            <div data-testid="activity-plan" className="space-y-0.5">
              <p className="text-xs font-medium text-foreground">
                Plan · {plan.filter((p) => p.estado === "hecho").length} de {plan.length}
              </p>
              <ul className="space-y-0.5">
                {plan.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs">
                    {p.estado === "hecho" ? <Check className="h-3.5 w-3.5 mt-0.5 text-emerald-500 shrink-0" /> : <Circle className="h-3.5 w-3.5 mt-0.5 shrink-0 opacity-50" />}
                    <span className={p.estado === "hecho" ? "line-through opacity-70" : ""}>{p.texto}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <ul className="space-y-1">
            {groups.map((group, i) => {
              if (group.items.length === 1) {
                const step = group.items[0];
                return (
                  <li key={i} data-testid={`activity-step-${i}`} className="flex items-start gap-2">
                    <Check className="h-3.5 w-3.5 mt-0.5 text-emerald-500 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <span>{step.label}</span>
                      {step.detail && (
                        <button
                          type="button"
                          data-testid={`activity-step-detail-${i}`}
                          onClick={() => setWide((cur) => ({ ...cur, [i]: !cur[i] }))}
                          title={wide[i] ? "Plegar" : "Ver completo"}
                          className={`mt-0.5 block w-full text-left rounded bg-muted/40 px-2 py-1 font-mono text-xs ${wide[i] ? "whitespace-pre-wrap break-all" : "truncate"}`}
                        >
                          {step.detail}
                        </button>
                      )}
                    </div>
                  </li>
                );
              }
              const expanded = !!groupOpen[i];
              return (
                <li key={i} data-testid={`activity-group-${i}`} className="flex items-start gap-2">
                  <Check className="h-3.5 w-3.5 mt-0.5 text-emerald-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      data-testid={`activity-group-toggle-${i}`}
                      aria-expanded={expanded}
                      onClick={() => setGroupOpen((cur) => ({ ...cur, [i]: !cur[i] }))}
                      className="flex items-center gap-1 text-left hover:text-foreground"
                    >
                      <span>{group.label}</span>
                      <span className="tabular-nums opacity-70">× {group.items.length}</span>
                      {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    </button>
                    {expanded && (
                      <ul className="mt-0.5 space-y-0.5">
                        {group.items.map((step, j) => (
                          <li key={j} data-testid={`activity-group-${i}-item-${j}`} className="rounded bg-muted/40 px-2 py-1 font-mono text-xs whitespace-pre-wrap break-all">
                            {step.detail || step.label}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
